import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CURRENCIES = [
  'USD','EUR','GBP','AUD','CAD','CHF','CNY','HKD','INR','JPY',
  'KRW','MXN','MYR','NOK','NZD','PHP','PLN','SAR','SEK','SGD',
  'THB','TRY','TWD','ZAR',
];

const emptyForm = {
  name: '', description: '', price: '', currency: 'USD',
  duration_days: '', device_limit: '', status: 'active',
};

export default function PlansPage() {
  const [plans,     setPlans]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [editId,    setEditId]    = useState(null);
  const [error,     setError]     = useState('');
  const [saving,    setSaving]    = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/plans');
      setPlans(data.plans);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };

  // const openEdit = (plan) => {
  //   setForm({
  //     name: plan.name, description: plan.description || '',
  //     price: plan.price, currency: plan.currency,
  //     duration_days: plan.duration_days, device_limit: plan.device_limit,
  //     status: plan.status,
  //   });
  //   setEditId(plan.id);
  //   setError('');
  //   setShowModal(true);
  // };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.put(`/admin/plans/${editId}`, form);
      } else {
        await api.post('/admin/plans', form);
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="table-card">
        {/* Header */}
        <div className="card-header">
          <span className="card-title">📋 Plans</span>
          <button className="action-btn" onClick={openCreate}>+ Add Plan</button>
        </div>

        {/* Table */}
        {loading ? (
          <p style={{ color: 'var(--text-muted)', padding: '20px 0', fontSize: 14 }}>Loading...</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Duration</th>
                  <th>Devices</th>
                  <th>Status</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>
                      No plans found
                    </td>
                  </tr>
                ) : plans.map((plan, i) => (
                  <tr key={plan.id}>
                    <td>{i + 1}</td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{plan.name}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {plan.description || <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>
                        {plan.currency} {parseFloat(plan.price).toFixed(2)}
                      </span>
                    </td>
                    <td>{plan.duration_days} days</td>
                    <td>{plan.device_limit}</td>
                    <td>
                      <span className={`status-badge ${plan.status === 'active' ? 'active' : 'expired'}`}>
                        {plan.status}
                      </span>
                    </td>
                    {/* <td>
                      <button className="edit-btn" onClick={() => openEdit(plan)}>Edit</button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
                {editId ? '✏️ Edit Plan' : '📋 Add Plan'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Plan Name</label>
                <input className="form-input" name="name" value={form.name}
                  onChange={handleChange} required placeholder="e.g. Guardian Plus" />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input className="form-input" name="description" value={form.description}
                  onChange={handleChange} placeholder="e.g. Basic security plan" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Price</label>
                  <input className="form-input" name="price" type="number" step="0.01" min="0"
                    value={form.price} onChange={handleChange} required placeholder="29.99" />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select className="form-input" name="currency" value={form.currency} onChange={handleChange} required>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Duration (Days)</label>
                  <input className="form-input" name="duration_days" type="number" min="1"
                    value={form.duration_days} onChange={handleChange} required placeholder="365" />
                </div>
                <div className="form-group">
                  <label>Device Limit</label>
                  <input className="form-input" name="device_limit" type="number" min="1"
                    value={form.device_limit} onChange={handleChange} required placeholder="3" />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select className="form-input" name="status" value={form.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {error && <p style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</p>}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="action-btn" disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
